import { useEffect, useRef } from "react"

import { MessageBubble } from "@/components/chat/MessageBubble"
import { ProjectPicker } from "@/components/chat/ProjectPicker"
import type { ChatMessage } from "@/hooks/useChatStream"
import { useAppConfig } from "@/lib/app-config"
import { useIdentity } from "@/lib/identity"
import { firstName } from "@/lib/text"
import type { Project } from "@/types/api"

const EXAMPLE_PROMPTS = [
  "What documents do we have about onboarding?",
  "Summarize the latest project status report",
  "Which guidelines exist for Azure best practices?",
]

export interface ProjectStarters {
  projectName: string
  prompts: string[]
  loading: boolean
}

export interface ProjectChoice {
  projects: Project[]
  selectedId: string | null
  onSelect: (projectId: string | null) => void
}

function EmptyState({
  onPrompt,
  projectStarters,
  projectChoice,
}: {
  onPrompt: (question: string) => void
  projectStarters?: ProjectStarters | null
  projectChoice?: ProjectChoice | null
}) {
  const { user } = useIdentity()
  const { title, description } = useAppConfig()
  const name = firstName(user)
  const inProject = Boolean(projectStarters)
  // Project chats get document-based starters; the generic SharePoint examples only
  // return once the project has produced none.
  const prompts = inProject && projectStarters!.prompts.length ? projectStarters!.prompts : description ? [] : EXAMPLE_PROMPTS
  const showPlaceholders = inProject && projectStarters!.loading

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center animate-in fade-in duration-300">
      <div className="space-y-1.5">
        <h2 className="text-2xl font-light tracking-tight text-foreground">Hello{name ? `, ${name}` : ""}!</h2>
        <p className="text-sm text-fg3">
          {inProject ? `New chat in “${projectStarters!.projectName}”.` : description || `Ask ${title} anything about your SharePoint documents.`}
        </p>
      </div>
      {projectChoice && (
        <ProjectPicker
          projects={projectChoice.projects}
          selectedId={projectChoice.selectedId}
          onSelect={projectChoice.onSelect}
        />
      )}
      <div className="flex max-w-md flex-wrap justify-center gap-2" aria-busy={showPlaceholders}>
        {showPlaceholders &&
          [0, 1, 2].map((i) => (
            <span key={i} className="h-7 w-44 animate-pulse rounded-full border border-border bg-card" aria-hidden="true" />
          ))}
        {/* The SharePoint example prompts only fit the default bot; agents with their own title show none */}
        {(showPlaceholders ? [] : prompts).map((prompt, i) => (
          <button
            key={prompt}
            type="button"
            onClick={() => onPrompt(prompt)}
            className="animate-in fade-in slide-in-from-bottom-2 fill-mode-both rounded-full border border-border bg-card px-3.5 py-1.5 text-xs text-fg2 transition-colors duration-300 hover:border-orange/50 hover:bg-hover hover:text-foreground"
            style={{ animationDelay: `${150 + i * 80}ms` }}
          >
            {prompt}
          </button>
        ))}
      </div>
    </div>
  )
}

export function ChatThread({
  messages,
  onSuggestionSelect,
  projectStarters,
  projectChoice,
}: {
  messages: ChatMessage[]
  onSuggestionSelect: (question: string) => void
  projectStarters?: ProjectStarters | null
  projectChoice?: ProjectChoice | null
}) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const stickToBottom = useRef(true)

  // Follow the stream only while the user is already near the bottom
  useEffect(() => {
    const el = scrollRef.current
    if (el && stickToBottom.current) {
      el.scrollTop = el.scrollHeight
    }
  }, [messages])

  const handleScroll = () => {
    const el = scrollRef.current
    if (!el) return
    stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  if (!messages.length) {
    return (
      <div className="flex-1 overflow-y-auto">
        <EmptyState onPrompt={onSuggestionSelect} projectStarters={projectStarters} projectChoice={projectChoice} />
      </div>
    )
  }

  return (
    <div ref={scrollRef} onScroll={handleScroll} className="flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-7 px-6 py-8">
        {messages.map((message) => (
          <MessageBubble key={message.id} message={message} onSuggestionSelect={onSuggestionSelect} />
        ))}
      </div>
    </div>
  )
}
